import React, { useMemo, memo, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel
} from '@tanstack/react-table';
import type { SortingState, ColumnFiltersState } from '@tanstack/react-table';
import type { DataGridProps, DataGridColumn } from './DataGrid.types';
import { TableHeader, type DensityType } from './components/TableHeader';
import { TableBody } from './components/TableBody';
import { VirtualTableBody } from './components/VirtualTableBody';
import { AggregationRow } from './components/AggregationRow';
import { useGrouping } from './hooks/useGrouping';
import { useAggregation } from './hooks/useAggregation';
import { useExport } from './hooks/useExport';
import { useSelection } from './hooks/useSelection';
import { useColumnVisibility } from './hooks/useColumnVisibility';
import { usePagination } from './hooks/usePagination';
import { useSearch } from './hooks/useSearch';
import { useColumnResize } from './hooks/useColumnResize';
import { useColumnReorder } from './hooks/useColumnReorder';
import { useColumnPinning } from './hooks/useColumnPinning';
import { ColumnToggle } from './components/ColumnToggle';
import { Pagination } from './components/Pagination';
import { SearchInput } from './components/SearchInput';

// Icons - Compact design
const ExpandIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M3 5h8M3 9h8M5 3v8M9 3v8" strokeLinecap="round" />
  </svg>
);

const CollapseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M3 7h8" strokeLinecap="round" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M7 2v6m0 0L4.5 5.5m2.5 2.5l2.5-2.5M3 10v1.5a1 1 0 001 1h6a1 1 0 001-1V10" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Density selector icons
const DensityCompactIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M2 3h10M2 6h10M2 9h10M2 12h10" strokeLinecap="round" />
  </svg>
);

const DensityNormalIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M2 3h10M2 7h10M2 11h10" strokeLinecap="round" />
  </svg>
);

const DensityComfortableIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M2 4h10M2 10h10" strokeLinecap="round" />
  </svg>
);

const LoadingSpinner = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'var(--space-12) var(--space-4)',
      gap: 'var(--space-3)'
    }}
  >
    <div
      style={{
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        border: '2px solid var(--color-primary-100)',
        borderTopColor: 'var(--color-primary)',
        animation: 'spin 0.8s linear infinite'
      }}
    />
    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
      Veriler yükleniyor...
    </span>
  </div>
);

const EmptyState = ({ message }: { message: string }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'var(--space-12) var(--space-4)',
      gap: 'var(--space-3)'
    }}
  >
    <div
      style={{
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        background: 'var(--color-neutral-100)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-neutral-400)" strokeWidth="1.5">
        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
    <div style={{ textAlign: 'center' }}>
      <p style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{message}</p>
      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', marginTop: 'var(--space-1)' }}>
        Filtreleri değiştirin veya yeni veri ekleyin
      </p>
    </div>
  </div>
);

// Status Bar Component
interface StatusBarProps {
  selectedCount: number;
  totalCount: number;
  filteredCount?: number;
  density: DensityType;
}

const StatusBar = memo(({ selectedCount, totalCount, filteredCount }: StatusBarProps) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '8px 16px',
      borderTop: '1px solid var(--color-border)',
      background: 'var(--color-bg-header)',
      fontSize: '12px',
      color: 'var(--color-text-secondary)',
      fontFamily: 'var(--font-mono)'
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
      {selectedCount > 0 && (
        <span style={{ color: 'var(--color-primary)' }}>
          {selectedCount.toLocaleString('tr-TR')} satır seçili
        </span>
      )}
      {filteredCount !== undefined && filteredCount !== totalCount && (
        <span>
          Filtreli: {filteredCount.toLocaleString('tr-TR')}
        </span>
      )}
    </div>
    <div>
      Toplam: {totalCount.toLocaleString('tr-TR')} satır
    </div>
  </div>
));

StatusBar.displayName = 'StatusBar';

// Density Selector Component
interface DensitySelectorProps {
  density: DensityType;
  onChange: (density: DensityType) => void;
}

const DensitySelector = memo(({ density, onChange }: DensitySelectorProps) => {
  const densities: { value: DensityType; icon: React.ReactNode; label: string }[] = [
    { value: 'compact', icon: <DensityCompactIcon />, label: 'Kompakt' },
    { value: 'normal', icon: <DensityNormalIcon />, label: 'Normal' },
    { value: 'comfortable', icon: <DensityComfortableIcon />, label: 'Rahat' }
  ];

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        padding: '2px',
        background: 'var(--color-bg-tertiary)',
        borderRadius: 'var(--radius-md)'
      }}
    >
      {densities.map((d) => (
        <button
          key={d.value}
          onClick={() => onChange(d.value)}
          title={d.label}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '6px',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            background: density === d.value ? 'var(--color-bg-card)' : 'transparent',
            color: density === d.value ? 'var(--color-primary)' : 'var(--color-text-muted)',
            cursor: 'pointer',
            transition: 'all var(--transition-fast)',
            boxShadow: density === d.value ? 'var(--shadow-sm)' : 'none'
          }}
        >
          {d.icon}
        </button>
      ))}
    </div>
  );
});

DensitySelector.displayName = 'DensitySelector';

// Storage helper functions
const STORAGE_PREFIX = 'gorkem-grid-';

interface GridPreferences {
  density?: DensityType;
  hiddenColumns?: string[];
}

function loadPreferences(storageKey: string | undefined): GridPreferences {
  if (!storageKey) return {};
  try {
    const stored = localStorage.getItem(`${STORAGE_PREFIX}${storageKey}`);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.warn('Failed to load grid preferences:', e);
  }
  return {};
}

function savePreferences(storageKey: string | undefined, prefs: GridPreferences): void {
  if (!storageKey) return;
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${storageKey}`, JSON.stringify(prefs));
  } catch (e) {
    console.warn('Failed to save grid preferences:', e);
  }
}

function DataGridComponent<T extends Record<string, unknown>>({
  data,
  columns,
  groupBy = [],
  aggregations = {},
  enableExcelExport = false,
  excelFilename = 'export.xlsx',
  enableSorting = true,
  enableFiltering = false,
  enableColumnResizing = false,
  enableColumnReorder = false,
  enableColumnPinning = false,
  enableRowSelection = false,
  enableColumnVisibility = false,
  enablePagination = false,
  enableSearch = false,
  pageSize = 10,
  onExport,
  onRowClick,
  onSelectionChange,
  className = '',
  headerClassName = '',
  rowClassName,
  emptyMessage = 'Veri bulunamadı',
  loading = false,
  // Virtualization
  enableVirtualization = false,
  virtualRowHeight = 48,
  virtualContainerHeight = 400,
  virtualOverscan = 5,
  // Enterprise Finance & B2B props
  density: propDensity = 'normal',
  theme = 'default',
  showStatusBar = false,
  enableCellFlash = false,
  cellStyle,
  // Persistence
  storageKey,
  // Grouping display options
  showGroupRowCount = true
}: DataGridProps<T>): React.ReactElement {
  // Load saved preferences on mount
  const savedPrefs = React.useMemo(() => loadPreferences(storageKey), [storageKey]);

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [density, setDensity] = React.useState<DensityType>(savedPrefs.density || propDensity);

  // Handle density change with persistence
  const handleDensityChange = React.useCallback((newDensity: DensityType) => {
    setDensity(newDensity);
    if (storageKey) {
      const currentPrefs = loadPreferences(storageKey);
      savePreferences(storageKey, { ...currentPrefs, density: newDensity });
    }
  }, [storageKey]);

  // Sync density with prop only if no saved preference
  useEffect(() => {
    if (!savedPrefs.density) {
      setDensity(propDensity);
    }
  }, [propDensity, savedPrefs.density]);

  // Selection hook
  const {
    isSelected,
    isAllSelected,
    isPartiallySelected,
    toggleRow,
    toggleAll,
    selectedRows
  } = useSelection({
    data,
    onSelectionChange
  });

  // Column visibility hook with persistence
  const {
    visibleColumns,
    hiddenColumnIds,
    toggleColumn: baseToggleColumn,
    showAllColumns: baseShowAll,
    hideAllColumns: baseHideAll
  } = useColumnVisibility({ columns, initialHiddenIds: savedPrefs.hiddenColumns });

  // Wrap column visibility functions to persist changes
  const toggleColumn = React.useCallback((columnId: string) => {
    baseToggleColumn(columnId);
    // Get updated hidden columns after toggle
    const newHidden = hiddenColumnIds.includes(columnId)
      ? hiddenColumnIds.filter(id => id !== columnId)
      : [...hiddenColumnIds, columnId];
    if (storageKey) {
      const currentPrefs = loadPreferences(storageKey);
      savePreferences(storageKey, { ...currentPrefs, hiddenColumns: newHidden });
    }
  }, [baseToggleColumn, hiddenColumnIds, storageKey]);

  const showAllColumns = React.useCallback(() => {
    baseShowAll();
    if (storageKey) {
      const currentPrefs = loadPreferences(storageKey);
      savePreferences(storageKey, { ...currentPrefs, hiddenColumns: [] });
    }
  }, [baseShowAll, storageKey]);

  const hideAllColumns = React.useCallback(() => {
    baseHideAll();
    if (storageKey) {
      const currentPrefs = loadPreferences(storageKey);
      const allColumnIds = columns.map(c => c.id);
      savePreferences(storageKey, { ...currentPrefs, hiddenColumns: allColumnIds });
    }
  }, [baseHideAll, columns, storageKey]);

  // Column resize hook
  const {
    resizingColumnId,
    getResizeHandler,
    getColumnWidth
  } = useColumnResize({ columns });

  // Column reorder hook
  const {
    orderedColumns,
    draggedColumnId,
    dropIndicatorIndex,
    getDragHandlers,
    getColumnStyle
  } = useColumnReorder({ columns });

  // Column pinning hook
  const {
    pinColumn,
    isPinned
  } = useColumnPinning({ columns });

  // Search hook
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchedData,
    clearSearch,
    matchCount
  } = useSearch({ data });

  // Use searched data for subsequent processing
  const processedData = enableSearch ? searchedData : data;

  // Use visible columns for TanStack table (respecting column order and visibility)
  const baseColumns = enableColumnReorder ? orderedColumns : columns;
  const activeColumns = enableColumnVisibility
    ? baseColumns.filter(col => visibleColumns.some(vc => vc.id === col.id))
    : baseColumns;

  const tanstackColumns = useMemo(() => {
    return activeColumns.map((col): DataGridColumn<T> => ({
      ...col,
      id: col.id,
      accessorKey: col.accessorKey,
      accessorFn: col.accessorFn,
      header: col.header,
      size: col.width,
      minSize: col.minWidth,
      maxSize: col.maxWidth,
      // Enable sorting for all columns that have an accessor
      enableSorting: enableSorting && (!!col.accessorKey || !!col.accessorFn)
    }));
  }, [activeColumns, enableSorting]);

  // TanStack Table expects ColumnDef[] type. Our DataGridColumn extends ColumnDef
  // but adds custom fields. The type assertion is necessary because TanStack's
  // strict typing doesn't recognize our extended column interface directly.
  // This is type-safe because DataGridColumn includes all required ColumnDef fields.
  const table = useReactTable({
    data: processedData,
    columns: tanstackColumns as Parameters<typeof useReactTable<T>>['0']['columns'],
    state: {
      sorting,
      columnFilters
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    // Always include getSortedRowModel when sorting is enabled
    getSortedRowModel: enableSorting ? getSortedRowModel() : getCoreRowModel(),
    getFilteredRowModel: enableFiltering ? getFilteredRowModel() : undefined,
    // Enable manual sorting to be false so TanStack handles sorting
    manualSorting: false
  });

  // Get sorted data from TanStack table when sorting is enabled
  // The rows from getRowModel() are already sorted by TanStack
  const tableRows = table.getRowModel().rows;
  const sortedData = useMemo(() => {
    if (enableSorting) {
      return tableRows.map(row => row.original as T);
    }
    return processedData;
  }, [enableSorting, tableRows, processedData]);

  // Pagination hook (uses sorted/searched data)
  const {
    currentPage,
    totalPages,
    pageSize: currentPageSize,
    paginatedData,
    goToPage,
    goToNextPage,
    goToPrevPage,
    goToFirstPage,
    goToLastPage,
    setPageSize: handlePageSizeChange,
    startIndex,
    endIndex,
    totalItems,
    canGoNext,
    canGoPrev
  } = usePagination({
    data: sortedData,
    pageSize,
    initialPage: 1
  });

  const {
    groupedData,
    expandedGroups,
    toggleGroup,
    flattenedRows,
    expandAll,
    collapseAll
  } = useGrouping({
    data: sortedData,
    groupBy,
    aggregations
  });

  // Use filtered/searched data for aggregations so totals update with search
  const { totalAggregations } = useAggregation({
    data: processedData,
    aggregations
  });

  const { exportToExcel, exportGroupedToExcel } = useExport({
    data,
    columns,
    groupedData,
    filename: excelFilename,
    onExport
  });

  const isGrouped = groupBy.length > 0;
  const hasAggregations = Object.keys(aggregations).length > 0;

  const handleExport = async () => {
    if (isGrouped) {
      await exportGroupedToExcel();
    } else {
      await exportToExcel();
    }
  };

  // Theme attribute for CSS targeting
  const themeAttribute = theme !== 'default' ? { 'data-grid-theme': theme } : {};

  // Memoized styles to prevent unnecessary re-renders
  const containerStyle = useMemo(() => ({
    background: 'var(--color-bg-card)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--color-border)',
    boxShadow: 'var(--shadow-md)',
    overflow: 'hidden'
  }), []);

  const loadingContainerStyle = useMemo(() => ({
    background: 'var(--color-bg-card)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--color-border)',
    boxShadow: 'var(--shadow-sm)'
  }), []);

  const toolbarStyle = useMemo(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 14px',
    borderBottom: '1px solid var(--color-border)',
    background: 'var(--color-bg-header)',
    gap: '12px'
  }), []);

  const tableStyle = useMemo(() => ({
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontFamily: 'var(--font-body)'
  }), []);

  if (loading) {
    return (
      <div
        className={`gorkem-data-grid ${className}`}
        {...themeAttribute}
        style={loadingContainerStyle}
      >
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div
      className={`gorkem-data-grid ${className}`}
      {...themeAttribute}
      style={containerStyle}
    >
      {/* Compact Toolbar */}
      {(enableExcelExport || isGrouped || enableColumnVisibility || enableSearch || (enableRowSelection && selectedRows.length > 0)) && (
        <div style={toolbarStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
            {/* Search Input */}
            {enableSearch && (
              <SearchInput
                value={searchTerm}
                onChange={setSearchTerm}
                onClear={clearSearch}
                placeholder="Ara..."
                matchCount={matchCount}
                totalCount={data.length}
              />
            )}

            {/* Selection count badge */}
            {enableRowSelection && selectedRows.length > 0 && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 10px',
                  borderRadius: 'var(--radius-full)',
                  background: 'var(--color-primary-100)',
                  color: 'var(--color-primary)',
                  fontSize: '12px',
                  fontWeight: 500,
                  fontFamily: 'var(--font-mono)'
                }}
              >
                {selectedRows.length}
              </span>
            )}
            {isGrouped && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <button
                  onClick={expandAll}
                  title="Tümünü Aç"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '6px',
                    border: 'none',
                    borderRadius: 'var(--radius-sm)',
                    background: 'transparent',
                    color: 'var(--color-text-secondary)',
                    cursor: 'pointer',
                    transition: 'all var(--transition-fast)'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-bg-hover)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <ExpandIcon />
                </button>
                <button
                  onClick={collapseAll}
                  title="Tümünü Kapat"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '6px',
                    border: 'none',
                    borderRadius: 'var(--radius-sm)',
                    background: 'transparent',
                    color: 'var(--color-text-secondary)',
                    cursor: 'pointer',
                    transition: 'all var(--transition-fast)'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-bg-hover)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <CollapseIcon />
                </button>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* Density Selector */}
            <DensitySelector density={density} onChange={handleDensityChange} />

            {/* Column visibility toggle */}
            {enableColumnVisibility && (
              <ColumnToggle
                columns={columns}
                hiddenColumnIds={hiddenColumnIds}
                onToggleColumn={toggleColumn}
                onShowAll={showAllColumns}
                onHideAll={hideAllColumns}
              />
            )}

            {enableExcelExport && (
              <button
                onClick={handleExport}
                title="Excel'e Aktar"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--color-positive)',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all var(--transition-fast)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-positive-light)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'var(--color-positive)'}
              >
                <DownloadIcon />
                <span>Excel</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Table Container */}
      <div style={{ overflowX: 'auto' }}>
        {enableVirtualization && !isGrouped ? (
          <>
            {/* Virtual Table Header */}
            <div
              style={{
                display: 'flex',
                background: 'var(--color-bg-header)',
                borderBottom: '1px solid var(--color-border)'
              }}
            >
              {enableRowSelection && (
                <div
                  style={{
                    width: 40,
                    minWidth: 40,
                    padding: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRight: '1px solid var(--color-border-light)'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = isPartiallySelected;
                    }}
                    onChange={toggleAll}
                    style={{ cursor: 'pointer' }}
                  />
                </div>
              )}
              {activeColumns.map((column) => (
                <div
                  key={column.id}
                  onClick={enableSorting ? () => {
                    const currentSort = sorting.find(s => s.id === column.id);
                    if (!currentSort) {
                      setSorting([{ id: column.id, desc: false }]);
                    } else if (!currentSort.desc) {
                      setSorting([{ id: column.id, desc: true }]);
                    } else {
                      setSorting([]);
                    }
                  } : undefined}
                  style={{
                    width: column.width || 150,
                    minWidth: column.minWidth || 50,
                    maxWidth: column.maxWidth,
                    padding: density === 'compact' ? '6px 10px' : '10px 14px',
                    fontSize: density === 'compact' ? '11px' : '12px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px',
                    color: 'var(--color-text-secondary)',
                    borderRight: '1px solid var(--color-border-light)',
                    cursor: enableSorting ? 'pointer' : 'default',
                    userSelect: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <span>{column.header}</span>
                  {enableSorting && (
                    <span style={{ opacity: 0.5, color: sorting.find(s => s.id === column.id) ? 'var(--color-primary)' : 'inherit' }}>
                      {sorting.find(s => s.id === column.id)?.desc === false && '↑'}
                      {sorting.find(s => s.id === column.id)?.desc === true && '↓'}
                    </span>
                  )}
                </div>
              ))}
            </div>
            {/* Virtual Table Body */}
            <VirtualTableBody
              rows={enablePagination ? paginatedData : sortedData}
              columns={activeColumns}
              onRowClick={onRowClick}
              rowClassName={rowClassName}
              rowHeight={density === 'compact' ? 32 : density === 'comfortable' ? 48 : virtualRowHeight}
              containerHeight={virtualContainerHeight}
              overscan={virtualOverscan}
              enableSelection={enableRowSelection}
              isSelected={isSelected}
              onToggleSelect={toggleRow}
            />
          </>
        ) : (
          <table
            role="grid"
            aria-label="Veri tablosu"
            aria-rowcount={data.length}
            aria-colcount={activeColumns.length + (enableRowSelection ? 1 : 0)}
            style={tableStyle}
          >
            <TableHeader
              headerGroups={table.getHeaderGroups()}
              columns={activeColumns}
              enableSorting={enableSorting}
              className={headerClassName}
              enableSelection={enableRowSelection}
              isAllSelected={isAllSelected}
              isPartiallySelected={isPartiallySelected}
              onToggleSelectAll={toggleAll}
              enableResize={enableColumnResizing}
              resizingColumnId={resizingColumnId}
              getResizeHandler={getResizeHandler}
              getColumnWidth={(id) => getColumnWidth({ id, header: '' } as DataGridColumn<T>)}
              enableReorder={enableColumnReorder}
              draggedColumnId={draggedColumnId}
              dropIndicatorIndex={dropIndicatorIndex}
              getDragHandlers={getDragHandlers}
              getColumnStyle={getColumnStyle}
              enablePinning={enableColumnPinning}
              isPinned={isPinned}
              onPinColumn={pinColumn}
              density={density}
            />
            <TableBody
              rows={isGrouped ? flattenedRows : (enablePagination ? paginatedData : sortedData)}
              columns={activeColumns}
              expandedGroups={expandedGroups}
              onToggleGroup={toggleGroup}
              aggregations={aggregations}
              onRowClick={onRowClick}
              rowClassName={rowClassName}
              isGrouped={isGrouped}
              enableSelection={enableRowSelection}
              isSelected={isSelected}
              onToggleSelect={toggleRow}
              draggedColumnId={draggedColumnId}
              getColumnStyle={getColumnStyle}
              density={density}
              enableCellFlash={enableCellFlash}
              cellStyle={cellStyle}
              showGroupRowCount={showGroupRowCount}
            />
            {hasAggregations && !isGrouped && (
              <AggregationRow columns={activeColumns} aggregations={totalAggregations} />
            )}
          </table>
        )}
      </div>

      {/* Pagination */}
      {enablePagination && !isGrouped && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={currentPageSize}
          startIndex={startIndex}
          endIndex={endIndex}
          totalItems={totalItems}
          canGoNext={canGoNext}
          canGoPrev={canGoPrev}
          onGoToPage={goToPage}
          onGoToNextPage={goToNextPage}
          onGoToPrevPage={goToPrevPage}
          onGoToFirstPage={goToFirstPage}
          onGoToLastPage={goToLastPage}
          onPageSizeChange={handlePageSizeChange}
        />
      )}

      {/* Status Bar */}
      {showStatusBar && (
        <StatusBar
          selectedCount={selectedRows.length}
          totalCount={data.length}
          filteredCount={enableSearch ? searchedData.length : undefined}
          density={density}
        />
      )}

      {/* Empty State */}
      {data.length === 0 && !loading && (
        <EmptyState message={emptyMessage} />
      )}

      {/* Screen reader announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0
        }}
      >
        {enableSearch && searchedData.length !== data.length && (
          <span>{searchedData.length} sonuç bulundu</span>
        )}
        {enableRowSelection && selectedRows.length > 0 && (
          <span>{selectedRows.length} satır seçili</span>
        )}
      </div>
    </div>
  );
}

// Memoized export for performance
export const DataGrid = memo(DataGridComponent) as typeof DataGridComponent;
